import React from 'react'
import classNames from 'classnames'
import AddIcon from 'transfix-icons/build/AddIcon'
import SearchListIcon from 'transfix-icons/build/SearchListIcon'
import { Button } from 'transfix-ui/components/Button'
import { ContextCard } from 'transfix-ui/components/ContextCard'
import { Typography, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { SavedSearchItem } from './components/SavedSearchItem'
import { ISavedSearch } from './types'

export interface ISavedSearchesProps {
  searches: ISavedSearch[]
  /**
   * If clicking the button to open the menu automatically saves the current search
   * This will also disable the Save Current Search button at the bottom of the menu
   */
  clickToSave?: boolean
  onSave: () => void
  onRemove: (id: string) => void
  /**
   * If the Save Current Search button within the menu should be disabled
   */
  disableSaveCurrentSearch?: boolean
  onSaveDefault?: (newDefault: ISavedSearch) => void
}

const useSavedSearchesStyles = makeStyles(theme => ({
  title: {
    padding: theme.spacing(2),
  },
  contextCard: {
    maxWidth: '720px',
    '& .card': {
      padding: '0 !important',
    },
    '& .card-title': {
      padding: theme.spacing(2),
      paddingBottom: 0,
    },
  },
  searchListContainer: {
    borderTop: `1px solid ${brandColors.coolGray3}`,
  },
  saveCurrentSearchContainer: {
    position: 'relative',
    cursor: 'pointer',
    padding: theme.spacing(1, 2),
    maxHeight: '35px',
    '&:hover, &:active, &:focus': {
      background: brandColors.coolGray1,
    },
    '& button': {
      justifyContent: 'flex-start',
    },
  },
  disableSaveCurrentSearchContainer: {
    cursor: 'default',
    '&:hover, &:active, &:focus': {
      background: 'none',
    },
    '& button.Mui-disabled:disabled': {
      background: 'none !important',
      '& svg, & span': {
        color: brandColors.coolGray4,
      },
    },
  },
  saveCurrentSearchButton: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}))

const SavedSearches = ({
  clickToSave = false,
  disableSaveCurrentSearch: disableSaveCurrentSearchProp,
  onRemove,
  onSave,
  onSaveDefault,
  searches,
}: ISavedSearchesProps) => {
  const classes = useSavedSearchesStyles()

  const disableSaveCurrentSearch: boolean = disableSaveCurrentSearchProp || clickToSave

  const saveCurrentSearchButtonClasses = classNames(classes.saveCurrentSearchContainer, {
    [classes.disableSaveCurrentSearchContainer]: disableSaveCurrentSearch,
  })

  const saveHandler = () => {
    onSave()
  }

  const triggerButtonProps = clickToSave ? { onClick: saveHandler } : { noPadding: true }

  const saveDefaultSearch = (newDefaultSearch: ISavedSearch) => {
    if (onSaveDefault) {
      onSaveDefault(newDefaultSearch)
    }
  }

  return (
    <ContextCard
      contextCardClassName={classes.contextCard}
      popperOptions={{ placement: 'bottom-start' }}
      triggerText={
        <Button color='tertiary' {...triggerButtonProps}>
          {clickToSave ? 'Save Search' : <SearchListIcon size='large' />}
        </Button>
      }
      hideDottedLines>
      <div>
        <div className={classes.title}>
          <Typography variant='subtitle2'>Saved Searches</Typography>
        </div>
        <div className={classes.searchListContainer}>
          {searches.map(search => {
            return (
              <SavedSearchItem
                key={search.id}
                search={search}
                onRemove={onRemove}
                onSaveDefault={saveDefaultSearch}
              />
            )
          })}
          <div className={saveCurrentSearchButtonClasses}>
            <Button
              noPadding
              fullWidth
              focusVisible
              color='tertiary'
              variant='text'
              onClick={saveHandler}
              disabled={disableSaveCurrentSearch}>
              <div className={classes.saveCurrentSearchButton}>
                <AddIcon size='medium' color='skyBlue6' />
                <Typography variant='body2' component='span' color='primary'>
                  Save Current Search
                </Typography>
              </div>
            </Button>
          </div>
        </div>
      </div>
    </ContextCard>
  )
}

export default SavedSearches
